/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/**
 * Actions returned by nextPipelineAction
 */
export const PIPELINE_ACTION = {
  APPLY: 'APPLY',
  WAIT: 'WAIT',
  DONE: 'DONE',
};

const has = (object, key) => Object.hasOwn(object, key);
/**
 * Decide what a command pipeline should do next.
 * Steps are checked in list order, the position in the list is the execution order.
 *
 * A step is
 * {
 *   command  - command key the step applies, ex: seg
 *   requires - other command keys the step reads, optional
 *   ready    - (context, run) => Boolean, whether the step can be applied now, optional
 *   apply    - (value, context, run) => Array of consumed command keys | undefined
 *   listenOnly - the step only receives a command value that another step reads, optional
 * }
 *
 * @param {Array} steps - ordered step list
 * @param {Object} pending - command values that have arrived and are not applied yet
 * @param {Array} remaining - command keys of the current run that are not applied yet
 * @param {Object} context - current state the steps read
 * @param {Object} run - { keys, pending } passed to ready
 * @returns {{type: String, step: Object}}
 */
export const nextPipelineAction = (steps, pending, remaining, context, run) => {
  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    const isPending = has(pending, step.command);
    const isExpected = remaining.includes(step.command);
    // a command that is neither in the url nor received has nothing to apply, skip it
    if (!step.listenOnly && (isPending || isExpected)) {
      // wait until every command the step reads has arrived
      const keys = [step.command, ...(step.requires ?? [])];
      const isMissing = keys.some((key) => remaining.includes(key) && !has(pending, key));
      if (isMissing || (step.ready && !step.ready(context, run))) {
        return { type: PIPELINE_ACTION.WAIT, step };
      }
      return { type: PIPELINE_ACTION.APPLY, step };
    }
  }
  return { type: PIPELINE_ACTION.DONE };
};
